if (!customElements.get('materials-promotion-slider')) {
  customElements.define('materials-promotion-slider', class MaterialsPromotionSlider extends HTMLElement {
    constructor() {
      super();
      this.viewport = null;
      this.previousButton = null;
      this.nextButton = null;
      this.intro = null;
      this.resizeObserver = null;
      this.measurementFrame = null;
      this.isSliderConnected = false;
      this.onPreviousClick = () => this.scroll(-1);
      this.onNextClick = () => this.scroll(1);
      this.onViewportScroll = () => this.updateButtons();
      this.onWindowResize = () => {
        this.updateButtons();
        this.scheduleIntroHeightSync();
      };
      this.onSectionLoad = (event) => {
        if (event.target === this || event.target?.contains?.(this)) {
          this.updateButtons();
          this.refreshMeasurementTargets();
          this.scheduleIntroHeightSync();
        }
      };
      this.onResizeObserved = () => this.scheduleIntroHeightSync();
    }

    connectedCallback() {
      if (this.isSliderConnected) return;

      this.viewport = this.querySelector('[data-promotion-viewport]');
      this.previousButton = this.querySelector('[data-promotion-previous]');
      this.nextButton = this.querySelector('[data-promotion-next]');
      this.intro = this.closest('.materials-countdown-promotion')?.querySelector('.materials-countdown-promotion__intro');

      if (!this.viewport || !this.previousButton || !this.nextButton) return;

      this.previousButton.addEventListener('click', this.onPreviousClick);
      this.nextButton.addEventListener('click', this.onNextClick);
      this.viewport.addEventListener('scroll', this.onViewportScroll, { passive: true });
      window.addEventListener('resize', this.onWindowResize);
      document.addEventListener('shopify:section:load', this.onSectionLoad);
      this.isSliderConnected = true;
      this.updateButtons();
      this.refreshMeasurementTargets();
      this.scheduleIntroHeightSync();
    }

    disconnectedCallback() {
      this.cancelIntroHeightSync();
      this.resizeObserver?.disconnect();
      this.resizeObserver = null;
      this.clearIntroHeight();

      if (!this.isSliderConnected) return;

      this.previousButton.removeEventListener('click', this.onPreviousClick);
      this.nextButton.removeEventListener('click', this.onNextClick);
      this.viewport.removeEventListener('scroll', this.onViewportScroll);
      window.removeEventListener('resize', this.onWindowResize);
      document.removeEventListener('shopify:section:load', this.onSectionLoad);
      this.isSliderConnected = false;
      this.viewport = null;
      this.previousButton = null;
      this.nextButton = null;
      this.intro = null;
    }

    scroll(direction) {
      if (!this.viewport) return;

      const firstItem = this.viewport.querySelector('li');
      const amount = firstItem ? firstItem.getBoundingClientRect().width : this.viewport.clientWidth;
      this.viewport.scrollBy({ left: direction * amount, behavior: 'smooth' });
    }

    updateButtons() {
      if (!this.viewport || !this.previousButton || !this.nextButton) return;

      const tolerance = 1;
      const hasOverflow = this.viewport.scrollWidth > this.viewport.clientWidth + tolerance;
      const atStart = this.viewport.scrollLeft <= tolerance;
      const atEnd = this.viewport.scrollLeft + this.viewport.clientWidth >= this.viewport.scrollWidth - tolerance;

      this.previousButton.disabled = !hasOverflow || atStart;
      this.nextButton.disabled = !hasOverflow || atEnd;
    }

    getMeasurementTargets() {
      if (!this.viewport) return [];

      return [...this.viewport.querySelectorAll('.materials-countdown-promotion__item')]
        .map((item) => item.querySelector('.card-wrapper') || item.querySelector('.card, .yg-product-card'))
        .filter(Boolean);
    }

    refreshMeasurementTargets() {
      if (!this.viewport || !('ResizeObserver' in window)) return;

      this.resizeObserver?.disconnect();
      this.resizeObserver = new ResizeObserver(this.onResizeObserved);
      this.resizeObserver.observe(this.viewport);
      this.getMeasurementTargets().forEach((target) => this.resizeObserver.observe(target));
    }

    scheduleIntroHeightSync() {
      if (!this.isSliderConnected || this.measurementFrame !== null) return;

      this.measurementFrame = window.requestAnimationFrame(() => {
        this.measurementFrame = null;
        this.syncIntroHeight();
      });
    }

    cancelIntroHeightSync() {
      if (this.measurementFrame === null) return;

      window.cancelAnimationFrame(this.measurementFrame);
      this.measurementFrame = null;
    }

    syncIntroHeight() {
      if (!this.intro || window.innerWidth < 990) {
        this.clearIntroHeight();
        return;
      }

      const maximumHeight = this.getMeasurementTargets().reduce((maximum, target) => {
        const height = target.getBoundingClientRect().height;
        return Number.isFinite(height) && height > 0 ? Math.max(maximum, height) : maximum;
      }, 0);

      if (maximumHeight > 0) {
        this.intro.style.setProperty('--materials-countdown-promotion-intro-height', `${maximumHeight}px`);
      } else {
        this.clearIntroHeight();
      }
    }

    clearIntroHeight() {
      this.intro?.style.removeProperty('--materials-countdown-promotion-intro-height');
    }
  });
}

const materialsCountdownError = 'Wrong syntax. Please enter the date with the syntax: yyyy-mm-dd or yyyy-mm-dd hh:mm.';

function parseMaterialsCountdownDate(value, timezone) {
  const normalizedValue = (value || '').trim().replace(/\s+/g, ' ');
  const match = normalizedValue.match(/^(\d{4})-(\d{2})-(\d{2})(?:\s+(\d{2}):(\d{2})(?::(\d{2}))?)?$/);
  if (!match) return null;

  const [, year, month, day, hours = '00', minutes = '00', seconds = '00'] = match;
  const dateParts = [Number(year), Number(month), Number(day), Number(hours), Number(minutes), Number(seconds)];
  const [yearNumber, monthNumber, dayNumber, hourNumber, minuteNumber, secondNumber] = dateParts;
  const calendarDate = new Date(Date.UTC(yearNumber, monthNumber - 1, dayNumber));

  if (
    calendarDate.getUTCFullYear() !== yearNumber ||
    calendarDate.getUTCMonth() !== monthNumber - 1 ||
    calendarDate.getUTCDate() !== dayNumber ||
    hourNumber > 23 ||
    minuteNumber > 59 ||
    secondNumber > 59
  ) return null;

  const offsetMatch = (timezone || '+0000').trim().match(/^([+-])(\d{2})(\d{2})$/);
  if (!offsetMatch) return null;

  const [, sign, offsetHours, offsetMinutes] = offsetMatch;
  const offset = (Number(offsetHours) * 60 + Number(offsetMinutes)) * 60 * 1000;
  const utcTimestamp = Date.UTC(yearNumber, monthNumber - 1, dayNumber, hourNumber, minuteNumber, secondNumber);
  return sign === '+' ? utcTimestamp - offset : utcTimestamp + offset;
}

function formatMaterialsCountdownValue(value) {
  return String(value).padStart(2, '0');
}

function initializeMaterialsCountdown(countdown) {
  if (countdown.dataset.countdownInitialized === 'true') return;
  countdown.dataset.countdownInitialized = 'true';

  const endTime = parseMaterialsCountdownDate(countdown.dataset.endtime, countdown.dataset.timezone);
  if (!endTime) {
    countdown.textContent = materialsCountdownError;
    return;
  }

  const days = countdown.querySelector('.countdown__days__value');
  const hours = countdown.querySelector('.countdown__hours__value');
  const minutes = countdown.querySelector('.countdown__minutes__value');
  const seconds = countdown.querySelector('.countdown__seconds__value');
  if (!days || !hours || !minutes || !seconds) return;

  const updateCountdown = () => {
    const remaining = endTime - Date.now();
    if (remaining <= 0) {
      window.clearInterval(countdown.intervalId);
      countdown.textContent = countdown.dataset.expiredMsg || 'This promotion has ended.';
      return;
    }

    const daysNumber = Math.floor(remaining / (1000 * 60 * 60 * 24));
    const hoursNumber = Math.floor((remaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutesNumber = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
    const secondsNumber = Math.floor((remaining % (1000 * 60)) / 1000);

    days.textContent = `${daysNumber}D`;
    hours.textContent = formatMaterialsCountdownValue(hoursNumber);
    minutes.textContent = formatMaterialsCountdownValue(minutesNumber);
    seconds.textContent = formatMaterialsCountdownValue(secondsNumber);
  };

  updateCountdown();
  countdown.intervalId = window.setInterval(updateCountdown, 1000);
}

function initializeMaterialsCountdowns(container = document) {
  container.querySelectorAll('[data-materials-countdown]').forEach(initializeMaterialsCountdown);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initializeMaterialsCountdowns());
} else {
  initializeMaterialsCountdowns();
}

document.addEventListener('shopify:section:load', (event) => initializeMaterialsCountdowns(event.target));
